import { LogIn, LogOut } from 'lucide-react';
import { useState } from 'react';
import { Modal } from '../components/Modal';
import { useAuth } from './OidcProvider';

/**
 * What a user sees when the access token expired and renewal did not replace it.
 *
 * Deliberately not a redirect. Both paths end at the provider when re-authentication is really
 * needed, and neither survives the navigation - the difference is who picks the moment. An
 * automatic redirect takes the page away mid-keystroke in an asset's metadata form or mid-upload,
 * neither of which keeps a draft anywhere. Here the user finishes the sentence and then clicks,
 * and the click often costs nothing at all because the renewal succeeds on the second attempt.
 *
 * `dismissable={false}` is what keeps it standing; see the prop's own comment for why Escape
 * needs more than a prevented `cancel`. The focus goes to the renewal button explicitly, because
 * otherwise it lands on the first focusable descendant - "Abmelden" - and one Enter would discard
 * the very input this dialog exists to preserve.
 *
 * What it does NOT do: stop the page behind it from working. `showModal()` makes that content
 * inert to input and focus; it does not stop timers. Module pages with a `refetchInterval` keep
 * polling, and with an expired token those requests go out without an `Authorization` header and
 * come back 401. Recorded in TASKS.md.
 */
export function SessionExpiredDialog() {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const retry = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await auth.reauthenticate();
    } catch {
      setFailed('Die Anmeldung war nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  // Sign-out reaches the network too, and from an expired session the network is often exactly
  // what is broken. Without this the click would be silent: `void auth.logout()` discards the
  // rejection, nothing navigates, and the dialog cannot be closed.
  const signOut = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await auth.logout();
    } catch {
      setFailed('Die Abmeldung konnte nicht abgeschlossen werden. Bitte Verbindung prüfen.');
    } finally {
      setBusy(false);
    }
  };

  return <Modal labelledBy="session-expired-title" className="compact-dialog" role="alertdialog" dismissable={false} onClose={() => undefined}>
    <h2 id="session-expired-title">Sitzung abgelaufen</h2>
    <p>
      Deine Anmeldung ist abgelaufen. Melde dich erneut an, um weiterzuarbeiten – gelingt das im
      Hintergrund, bleiben ungespeicherte Eingaben erhalten. Sonst führt der Weg über Authentik.
    </p>
    {failed && <p className="error-copy" role="alert">{failed}</p>}
    <div className="dialog-actions">
      <button className="button ghost" disabled={busy} onClick={() => void signOut()}><LogOut size={17} /> Abmelden</button>
      <button className="button primary" disabled={busy} onClick={() => void retry()} data-initial-focus><LogIn size={17} /> Erneut anmelden</button>
    </div>
  </Modal>;
}
